import styled from "@emotion/styled";
import {
  Button,
  CircularProgress,
  debounce,
  FormControl,
  FormHelperText,
  IconButton,
  InputLabel,
  MenuItem,
  Modal,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { Box } from "@mui/system";
import RemoveCircleIcon from "@mui/icons-material/RemoveCircle";
import axios from "axios";
import { useCallback, useContext, useEffect, useState } from "react";
import SearchableSelect from "../SearchableSelect";
import createTicketSchema from "./createTicketSchema";
import { AppContext } from "../../scripts/context/App";
import toast from "react-hot-toast";

const style = {
  position: "absolute" as "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "90%",
  maxWidth: 700,
  minHeight: 400,
  bgcolor: "#fff",
  borderRadius: "4px",
  boxShadow: 24,
  display: "flex",
  flexDirection: "column",
};

const initTicketData = {
  user_id: 0,
  category_id: 0,
  title: "",
  message: "",
  attachment: null,
};

interface ICategory {
  id: number | string;
  name: string;
}

const initTicketsError = {
  user_id: false,
  category_id: false,
  title: false,
  message: false,
  attachment: false,
};

const Input = styled("input")`
  display: none;
`;

export default function CreateNewTicket({
  isOpen,
  handleClose,
  updateTickets,
}: {
  isOpen: boolean;
  handleClose: () => void;
  updateTickets: (object: any) => void;
}) {
  const { user } = useContext(AppContext);
  const [categories, setCategories] = useState<any[]>([]);
  const [ticketData, setTicketData] = useState<any>(initTicketData);
  const [isCreating, setIsCreating] = useState(false);
  const [ticketError, setTicketError] = useState<any>(initTicketsError);
  const [users, setUsers] = useState([]);
  const [searching, setSearching] = useState(false);

  const handleChanges = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value, maxLength } = event.target;
    if (maxLength && value.length >= maxLength) {
      return setTicketData({ ...ticketData, [name]: ticketData[name] });
    }

    setTicketData({ ...ticketData, [name]: value });
  };

  const searchUser = useCallback(
    debounce(async (event) => {
      setSearching(true);

      const { data } = await axios.get("/support-tickets/get-users", {
        params: { search_key: event.target.value },
      });
      setSearching(false);
      setUsers(
        data.data.data.map((user: { id: number; name: string }) => ({
          value: user.id,
          label: user.name,
        }))
      );
    }, 800),
    []
  );

  const getUsers = async () => {
    const { data } = await axios.get("/support-tickets/get-users");

    setUsers(
      data.data.data.map((user: { id: number; name: string }) => ({
        value: user.id,
        label: user.name,
      }))
    );
  };
  const getCategories = async () => {
    if (!user.permissions.includes("view_support_category"))
      return toast.error("You don't have permission to view ticket category.");

    try {
      const {
        data: { data: responseData },
      } = await axios.get("/support-tickets/get-categories");

      setCategories(responseData);
    } catch (error: any) {
      toast.error(
        error.response?.data?.message ||
        "Something went wrong. Please try again later."
      );

      console.log(error);
    }
  };

  const getInitData = async () => {
    await getCategories();
    await getUsers();
  };

  useEffect(() => {
    getInitData();
  }, []);

  const postNewTicket = async () => {
    if (!user.permissions.includes("create_ticket"))
      return toast.error("You don't have permission to create tickets");

    setTicketError(initTicketsError);
    try {
      await createTicketSchema.validate(ticketData, { abortEarly: false });

      const formData = new FormData();
      Object.keys(ticketData).forEach((key) => {
        ticketData[key] && formData.append(key, ticketData[key]);
      });

      setIsCreating(true);
      const { data } = await axios.post(
        "/support-tickets/new-ticket",
        formData
      );

      setIsCreating(false);
      updateTickets(data.data);
      handleClose();
      setTicketData(initTicketData);
    } catch (error: any) {
      setIsCreating(false);

      if (error.inner) {
        setTicketError(
          error.inner.reduce((acc: any, err: any) => {
            acc[err.path] = err.message;
            return acc;
          }, {})
        );
      } else {
        toast.error(
          error.response?.data?.message ||
          "Something went wrong. Please try again later."
        );
      }
    }
  };

  return (
    <Modal
      keepMounted
      open={isOpen}
      onClose={handleClose}
      aria-labelledby="create-new-ticket"
    >
      <Box sx={style}>
        <Box sx={{ p: 3, borderBottom: "1px solid #d3d3d3" }}>
          <Typography variant="h6">Create New Ticket</Typography>
        </Box>
        <Box
          sx={{
            p: 3,
            display: "flex",
            flexDirection: "column",
            gap: 1,
            flex: 1,
            overflowY: "auto",
            maxHeight: "60vh",
          }}
        >
          <FormControl error={Boolean(ticketError.category_id)} fullWidth>
            <InputLabel
              variant="standard"
              id="demo-simple-select-category_id-label"
            >
              Category
            </InputLabel>
            <Select
              labelId="category-simple-select-filled-label"
              id="category-simple-select-filled"
              value={ticketData.category_id}
              onChange={(event) => {
                setTicketData({
                  ...ticketData,
                  category_id: event.target.value,
                });
              }}
              variant="standard"
            >
              <MenuItem selected disabled>
                <em>Select Category</em>
              </MenuItem>
              {categories.length > 0 ? (
                categories.map((category: ICategory) => (
                  <MenuItem
                    value={category.id}
                    sx={{
                      maxWidth: "650px",
                    }}
                  >
                    <Typography
                      variant="body1"
                      color="textPrimary"
                      sx={{
                        textOverflow: "ellipsis",
                        overflow: "hidden",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {category.name}
                    </Typography>
                  </MenuItem>
                ))
              ) : (
                <MenuItem value={1}>
                  <em>No Categories Found</em>
                </MenuItem>
              )}
            </Select>
            {Boolean(ticketError.category_id) && (
              <FormHelperText>{ticketError.category_id}</FormHelperText>
            )}
          </FormControl>
          <SearchableSelect
            inputLabel="Choose User"
            searchInputLabel="Search User..."
            handleSearch={searchUser}
            items={users}
            handleSelect={(event) =>
              setTicketData({ ...ticketData, user_id: event.target.value })
            }
            loading={searching}
            initValue={ticketData.user_id}
            errorMessage={ticketError.user_id}
          />
          <TextField
            fullWidth
            id="filled-basic"
            label="Title"
            variant="standard"
            name="title"
            value={ticketData.title}
            onChange={handleChanges}
            error={Boolean(ticketError.title)}
            helperText={ticketError.title}
            inputProps={{ maxLength: 100 }}
          />
          <TextField
            fullWidth
            id="filled-basic"
            label="Message"
            variant="standard"
            name="message"
            value={ticketData.message}
            onChange={handleChanges}
            error={Boolean(ticketError.message)}
            helperText={ticketError.message}
            multiline
            maxRows={4}
            rows={4}
            inputProps={{ maxLength: 2000, style: { resize: "vertical" } }}
          />
          <Box>
            <label htmlFor="contained-button-file">
              <Input
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  setTicketError({ ...ticketError, attachment: false });
                  if (file && file.size > 5 * 1024 * 1024) {
                    return toast.error(
                      "Please select an image file with size less than 5MB"
                    )
                  }
                  if (file && file.type.includes("image")) {
                    setTicketData({
                      ...ticketData,
                      attachment: event.target.files?.[0],
                    });
                  } else {
                    setTicketError({
                      ...ticketError,
                      attachment: "Please upload an image",
                    });
                  }
                }}
                accept="image/jpg, image/jpeg, image/png"
                id="contained-button-file"
                multiple
                type="file"
              />
              <Button variant="contained" component="span">
                Attachment
              </Button>
            </label>
          </Box>
          {ticketError.attachment ? (
            <FormHelperText>{ticketError.attachment}</FormHelperText>
          ) : (
            ticketData.attachment && (
              <Box>
                <img
                  style={{
                    width: "100%",
                    maxWidth: "400px",
                    height: "200px",
                    objectFit: "cover",
                    borderRadius: "8px",
                  }}
                  src={URL.createObjectURL(ticketData.attachment)}
                  alt={ticketData.attachment.name}
                />
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <IconButton
                    color="error"
                    onClick={() => {
                      setTicketData({
                        ...ticketData,
                        attachment: null,
                      });
                    }}
                  >
                    <RemoveCircleIcon />
                  </IconButton>
                  <Typography variant="body1">
                    {ticketData.attachment.name}
                  </Typography>
                </Box>
              </Box>
            )
          )}
        </Box>
        <Box
          sx={{
            borderTop: "1px solid #d3d3d3",
            p: 3,
            display: "flex",
            justifyContent: "flex-end",
            gap: 1,
          }}
        >
          <Button
            variant="contained"
            color="primary"
            onClick={postNewTicket}
            disabled={!user.permissions.includes("create_ticket") || isCreating}
            endIcon={isCreating && <CircularProgress size={20} />}
          >
            Create
          </Button>
          <Button
            variant="outlined"
            color="error"
            disabled={isCreating}
            onClick={() => {
              setTicketData(initTicketData);
              setTicketError(initTicketsError);

              handleClose();
            }}
          >
            Cancel
          </Button>
        </Box>
      </Box>
    </Modal>
  );
}
